import { ref } from 'vue';
import Cookies from 'js-cookie';
import { usePlatformAPI } from '@/composables/api/usePlatformAPI';

/**
 * Composable que gestiona el estado y la carga de la plataforma/curso y sus ofertas.
 * Responsabilidad: carga de datos de plataforma + ofertas, formateo y estado de carga.
 */
export function usePlatformDetails() {
  const { getPlatform, getPlatformOffers } = usePlatformAPI();

  const platform = ref({});
  const offers = ref([]);
  const loadingData = ref(true);
  const loadingOffers = ref(false);

  /**
   * Formatea un número como moneda venezolana.
   * @param {number|string} value
   * @returns {string}
   */
  const _formatAmount = (value) => {
    if (!value) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '';
    return num.toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /**
   * Formatea una cantidad entera como entero venezolano.
   * @param {number|string} value
   * @returns {string}
   */
  const _formatQuantity = (value) => {
    if (value === 0 || value === '0') return '0';
    if (!value) return '';
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (isNaN(num)) return '';
    return num.toLocaleString('es-VE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  /**
   * Calcula el monto efectivo de una oferta según las fechas de preventas activas.
   * @param {Object} offer - Oferta raw del API
   * @returns {{ amount: number, priceType: string }}
   */
  const _resolveEffectiveAmount = (offer) => {
    const now = new Date();
    const baseAmount = Number(offer.amount || 0);
    let effectiveAmount = baseAmount;
    let priceType = 'Monto regular';

    if (offer.presale_1_start_date && offer.presale_1_end_date) {
      const p1Start = new Date(offer.presale_1_start_date);
      const p1End = new Date(offer.presale_1_end_date);
      if (now >= p1Start && now <= p1End && Number(offer.presale_1_amount || 0) > 0) {
        priceType = 'Preventa 1';
        effectiveAmount = Number(offer.presale_1_amount || baseAmount);
      }
    }

    if (offer.presale_2_start_date && offer.presale_2_end_date) {
      const p2Start = new Date(offer.presale_2_start_date);
      const p2End = new Date(offer.presale_2_end_date);
      if (now >= p2Start && now <= p2End && Number(offer.presale_2_amount || 0) > 0) {
        priceType = 'Preventa 2';
        effectiveAmount = Number(offer.presale_2_amount || baseAmount);
      }
    }

    return { amount: effectiveAmount, priceType };
  };

  /**
   * Transforma una oferta raw del API a la estructura normalizada para el template.
   * @param {Object} offer
   * @returns {Object}
   */
  const _mapOffer = (offer) => {
    const { amount: effectiveAmount, priceType } = _resolveEffectiveAmount(offer);

    let cityName = 'Venezuela';
    if (offer.city && offer.city !== 0) {
      if (offer.city_relation?.name) {
        cityName = offer.city_relation.name;
      }
    }

    // Procesamiento optimizado y perezoso según banderas del backend
    const check_enrollment = offer.check_enrollment || 0;
    const check_installments = offer.check_installments || 0;

    const enrollment_amount = check_enrollment === 1 ? Number(offer.enrollment_amount || 0) : 0;
    const enrollment_amount_formatted = check_enrollment === 1 ? _formatAmount(enrollment_amount) : '';

    const installments = (check_installments === 1 && Array.isArray(offer.installments))
      ? offer.installments.map((inst) => ({
          id: inst.id,
          amount: Number(inst.amount || 0),
          amountFormatted: _formatAmount(Number(inst.amount || 0)),
          due_date: inst.due_date,
        }))
      : [];

    // Calcular inscritos y cupos restantes usando user_platforms desde platform.value
    const userPlatforms = platform.value?.user_platforms || [];
    const currentUserId = Number(Cookies.get('id'));
    const registeredCount = userPlatforms.filter(
      (up) => Number(up.id_platform_offer) === Number(offer.id)
    ).length;
    const remainingSlots = Math.max(0, Number(offer.quantity || 0) - registeredCount);
    const isUserEnrolled = currentUserId > 0 && userPlatforms.some(
      (up) => Number(up.id_user) === currentUserId && Number(up.id_platform_offer) === Number(offer.id)
    );

    return {
      id: offer.id,
      amount: effectiveAmount,
      amountFormatted: _formatAmount(effectiveAmount),
      quantity: offer.quantity,
      quantityFormatted: _formatQuantity(offer.quantity),
      registeredCount,
      registeredCountFormatted: _formatQuantity(registeredCount),
      remainingSlots,
      remainingSlotsFormatted: _formatQuantity(remainingSlots),
      isUserEnrolled,
      stateId: offer.state,
      stateName: offer.state_relation?.name || 'Venezuela',
      cityId: offer.city,
      cityName,
      priceType,
      presale_1_start_date: offer.presale_1_start_date,
      presale_1_end_date: offer.presale_1_end_date,
      presale_1_amount: offer.presale_1_amount || 0,
      presale_2_start_date: offer.presale_2_start_date,
      presale_2_end_date: offer.presale_2_end_date,
      presale_2_amount: offer.presale_2_amount || 0,
      check_enrollment,
      check_installments,
      enrollment_amount,
      enrollment_amount_formatted,
      installments,
    };
  };

  /**
   * Carga las ofertas de una plataforma por su ID.
   * @param {string|number} platformId
   */
  const loadOffers = async (platformId) => {
    loadingOffers.value = true;
    try {
      const response = await getPlatformOffers(platformId);
      if (response?.code === 200 && Array.isArray(response.data)) {
        offers.value = response.data.map(_mapOffer);
      } else {
        offers.value = [];
      }
    } catch (error) {
      console.warn('Error cargando ofertas:', error);
      offers.value = [];
    } finally {
      loadingOffers.value = false;
    }
  };

  /**
   * Carga el detalle de la plataforma y sus ofertas por ID.
   * @param {string|number} id
   */
  const loadPlatform = async (id) => {
    loadingData.value = true;
    try {
      const response = await getPlatform(id);
      if (response?.code === 200) {
        const data = response.data;
        data.amount = parseFloat(data.amount || 0).toLocaleString('es-VE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        platform.value = data;
        await loadOffers(id);
      }
    } catch (error) {
      console.error('Error cargando plataforma:', error);
    } finally {
      loadingData.value = false;
    }
  };

  return {
    platform,
    offers,
    loadingData,
    loadingOffers,
    loadPlatform,
  };
}
